import React from "react";
import {
  Box,
  Checkbox,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { AlertData, HeadCell } from "../Alerts/AlertsUtils/alertsInterfaces";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useAppStore } from "../../../appStore";

const headCells: readonly HeadCell[] = [
  {
    id: "display_name",
    numeric: false,
    label: "Site",
  },
  {
    id: "camera_name",
    numeric: false,
    label: "Camera",
  },
  {
    id: "formatted_start_date",
    numeric: false,
    label: `Start Time`,
  },
  {
    id: "formatted_end_date",
    numeric: false,
    label: `End Time`,
  },
  {
    id: "error_type",
    numeric: false,
    label: "Alert Type",
  },
  {
    id: "severity",
    numeric: true,
    label: "Severity",
  },
  {
    id: "status",
    numeric: false,
    label: "Status",
  },
];

export const EnhancedAlertsTableHeadHM = ({
  onRequestSort,
  orderBy,
  order,
  activeData,
  setSelected,
  setAlertsArray,
}: {
  onRequestSort: any;
  orderBy: any;
  order: any;
  activeData: any;
  setSelected: any;
  setAlertsArray: any;
}) => {
  const { setDateMMDDYYYY } = useAppStore();

  // State and handlers for Start Time menu
  const [startTimeAnchorEl, setStartTimeAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const startTimeOpen = Boolean(startTimeAnchorEl);
  const handleStartTimeClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Stop event propagation to prevent table sorting
    setStartTimeAnchorEl(event.currentTarget);
  };
  const handleStartTimeClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Stop event propagation to prevent table sorting
    setStartTimeAnchorEl(null);
  };

  // State and handlers for End Time menu
  const [endTimeAnchorEl, setEndTimeAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const endTimeOpen = Boolean(endTimeAnchorEl);
  const handleEndTimeClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Stop event propagation to prevent table sorting
    setEndTimeAnchorEl(event.currentTarget);
  };
  const handleEndTimeClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Stop event propagation to prevent table sorting
    setEndTimeAnchorEl(null);
  };

  const createSortHandler =
    (property: keyof AlertData) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  const handleSelectAllClick = (event: any) => {
    if (event.target.checked) {
      setAlertsArray(activeData);
      const newSelecteds = activeData.map((obj: any) => obj.start_timestamp);
      setSelected(newSelecteds);
      return;
    }
    if (!event.target.checked) {
      setAlertsArray([]);
    }
    setSelected([]);
  };

  // Modified headCells with menu components
  const getHeadCellLabel = (headCell: HeadCell) => {
    if (headCell.id === "formatted_start_date") {
      return (
        <div className="flex">
          <>Start Time</>
          <div>
            <IconButton
              aria-label="more"
              id="start-time-button"
              aria-controls={startTimeOpen ? "start-time-menu" : undefined}
              aria-expanded={startTimeOpen ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleStartTimeClick}
              sx={{
                width: "18px",
                height: "18px",
                padding: "0px",
              }}
            >
              <MoreVertIcon sx={{ fontSize: "18px" }} />
            </IconButton>
            <Menu
              id="start-time-menu"
              MenuListProps={{
                "aria-labelledby": "start-time-button",
              }}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              anchorEl={startTimeAnchorEl}
              open={startTimeOpen}
              onClose={handleStartTimeClose}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: 48 * 4.5,
                    width: "20ch",
                  },
                },
              }}
            >
              <MenuItem
                key="mm-dd-yyyy-start"
                onClick={(event) => {
                  setDateMMDDYYYY(true);
                  event.stopPropagation(); // Stop event propagation to prevent table sorting
                  handleStartTimeClose(event);
                }}
              >
                MM/DD/YYYY
              </MenuItem>
              <MenuItem
                key="dd-mm-yyyy-start"
                onClick={(event) => {
                  setDateMMDDYYYY(false);
                  event.stopPropagation(); // Stop event propagation to prevent table sorting
                  handleStartTimeClose(event);
                }}
              >
                DD/MM/YYYY
              </MenuItem>
            </Menu>
          </div>
        </div>
      );
    } else if (headCell.id === "formatted_end_date") {
      return (
        <div className="flex">
          <>End Time</>
          <div>
            <IconButton
              aria-label="more"
              id="end-time-button"
              aria-controls={endTimeOpen ? "end-time-menu" : undefined}
              aria-expanded={endTimeOpen ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleEndTimeClick}
              sx={{
                width: "18px",
                height: "18px",
                padding: "0px",
              }}
            >
              <MoreVertIcon sx={{ fontSize: "18px" }} />
            </IconButton>
            <Menu
              id="end-time-menu"
              MenuListProps={{
                "aria-labelledby": "end-time-button",
              }}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              anchorEl={endTimeAnchorEl}
              open={endTimeOpen}
              onClose={handleEndTimeClose}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: 48 * 4.5,
                    width: "20ch",
                  },
                },
              }}
            >
              <MenuItem
                key="mm-dd-yyyy-end"
                onClick={(event) => {
                  setDateMMDDYYYY(true);
                  event.stopPropagation(); // Stop event propagation to prevent table sorting
                  handleEndTimeClose(event);
                }}
              >
                MM/DD/YYYY
              </MenuItem>
              <MenuItem
                key="dd-mm-yyyy-end"
                onClick={(event) => {
                  setDateMMDDYYYY(false);
                  event.stopPropagation(); // Stop event propagation to prevent table sorting
                  handleEndTimeClose(event);
                }}
              >
                DD/MM/YYYY
              </MenuItem>
            </Menu>
          </div>
        </div>
      );
    } else {
      return headCell.label;
    }
  };

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            onClick={(event) => {
              handleSelectAllClick(event);
            }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            // className="font-bold"
            key={headCell.id}
            align="left"
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{ fontFamily: "mulish", fontWeight: 700, width: 208 }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {getHeadCellLabel(headCell)}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};
